/** TODO
 * MODELS HAVE BEEN MOVED TO COMMON-LIB; REPLACE IMPORTS AND REMOVE THIS FILE
 */
import { IAskSchema } from './ask.model';
import { DateRange, SupportedLanguagesType } from '@app/core/interfaces';
import { HistoryAction } from 'common-lib';

export const DefaultWorkDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'] as const;
export const WeekDays = ['sunday', ...DefaultWorkDays, 'saturday'] as const;
export type WeekDay = typeof WeekDays[number];
export type IWorkDaysSchema = { [weekDay in WeekDay]: boolean };

export interface IStartOffsetsSchema {
    m00: boolean;
    m15: boolean;
    m30: boolean;
    m45: boolean;
}

export interface ICalculatedTime {
    value: string;
    name: string;
    selected?: boolean;
}

export interface IDuration {
    name: string;
    value: number;
}

export interface IMeetingTypeField<T> {
    value?: T;
}

export interface IMeetingTypeLearnedField<T> extends IMeetingTypeField<T> {
    managed?: boolean;
}

export interface IMaxEventsPerPeriod {
    days: {
        [weekDay in WeekDay]?: number;
    };
}

export interface IBufferTimes {
    beforeMinutes: number;
    afterMinutes: number;
}

export interface IUserVideoProvider {
    id: string;
    staticUrl: string;
}

export interface IUserPaymentProvider {
    id: string;
    amount: number;
    currency?: string | 'usd' | 'cad';
    terms?: string;
    autoRefund?: boolean;
}

export interface IAvailabilityUser {
    id: string;
    name: string;
}

export interface IMeetingTypeAsk {
    questions?: IAskSchema[];
    // TODO: add responses
}

export interface IStartEndTimeWindowSchema {
    start: string; // format 'HH:mm'
    end: string; // format 'HH:mm'
}

export type IAvailabilityWindowSchema = { [weekDay in WeekDay]: IStartEndTimeWindowSchema[] };

export interface IStartOffsetStartTime {
    kind: 'offsets';
    offsets: IStartOffsetsSchema;
}

export interface IIncrementStartTime {
    kind: 'increment';
    increment: number;
}

type ConfirmationOptionsType = 'default' | 'redirect';
export interface IMeetingTypeConfirmationUX {
    option: ConfirmationOptionsType;
    redirectSeconds?: number;
    redirectURL?: string;
}

export interface ITrackingId {
    type: 'google';
    id: string;
}

export interface IMeetingTypeBase {
    locked?: IMeetingTypeField<boolean>;
    datePinnedToDashboard?: IMeetingTypeField<string>;

    // Name and Link
    defaultMeetingSubject?: IMeetingTypeField<string>;
    ondemand?: IMeetingTypeField<string>; // short code for link

    // Booking Details
    bookingCalendarId?: IMeetingTypeField<string>;
    meetingLength?: IMeetingTypeLearnedField<number>;

    // Location & Conferencing
    location?: IMeetingTypeLearnedField<string>;
    roomCapacity?: IMeetingTypeField<number>;
    startTime?: IMeetingTypeField<IStartOffsetStartTime | IIncrementStartTime>;
    videoProvider?: IMeetingTypeField<IUserVideoProvider>;

    // Additional Customization
    language?: IMeetingTypeField<SupportedLanguagesType>;
    description?: IMeetingTypeField<string>; // Meeting Instructions
    confirmationUX?: IMeetingTypeField<IMeetingTypeConfirmationUX>;
    ask?: IMeetingTypeField<IMeetingTypeAsk>; // List of questions to ask the meeting attendees
    trackingIds?: IMeetingTypeField<ITrackingId[]>;

    // Collect Payments
    paymentProvider?: IMeetingTypeField<IUserPaymentProvider>;
}

export interface IMeetingTypeCommon extends IMeetingTypeBase {
    startHour?: IMeetingTypeLearnedField<string>; // TODO: Will be deprecated when availabilityWindows released
    endHour?: IMeetingTypeLearnedField<string>; // TODO: Will be deprecated when availabilityWindows released
    availabilityWindows?: IMeetingTypeLearnedField<IAvailabilityWindowSchema>;

    /**
     * @deprecated Use `availabilityWindows` instead
     */
    days?: Readonly<IMeetingTypeLearnedField<IWorkDaysSchema>>;

    allowEditingLocation?: IMeetingTypeField<boolean>; // Allow invitees to edit meeting location
    disableInvitingAttendees?: IMeetingTypeField<boolean>; // Allows invitees to add additional attendees

    // currently only used in the default meeting type
    dailyFreeRatio?: IMeetingTypeField<number>;
    // currently only used in the default meeting type
    disableTravelTime?: IMeetingTypeField<boolean>;

    noticePeriodMinutes?: IMeetingTypeField<number>; // lead time
    dateRange?: IMeetingTypeField<DateRange>;
    availabilityType?: IMeetingTypeField<AvailabilityType>;
    disableTransferOwnership?: IMeetingTypeField<boolean>;

    /**
     * this is only used when `availabilityType` is set to
     * `some-internal-users-collective`, `some-internal-users-round-robin`, 'on-behalf', or 'external-on-behalf'
     */
    availabilityUserIds?: IMeetingTypeField<string[]>;

    bufferTimes?: IMeetingTypeField<IBufferTimes>;

    availabilityPattern?: IMeetingTypeField<AvailabilityPattern>;

    enableRoundRobinSelection?: IMeetingTypeField<boolean>;
    bookingCalendarId?: IMeetingTypeField<string>;

    maxEventsPerPeriod?: IMeetingTypeField<IMaxEventsPerPeriod>;

    enableHolidayBlocking?: IMeetingTypeField<boolean>; // Allows ability to automatically remove availabilities based on user country holidays. true = block availabilities from holidays, false = don't block availabilities based on holidays

    readonly history: HistoryAction[];

    readonly shareWithNewTeamMembers?: boolean;
}

export enum MeetingTypeStyle {
    Meeting = 'meeting',
    Class = 'class'
}
export interface IMeetingType extends IMeetingTypeCommon {
    style: MeetingTypeStyle.Meeting;
}

export interface IDefaultMeetingType extends IMeetingTypeCommon {
    emailHour: number;
    notifyForOverlap: boolean;
    cc_meeting: boolean;
    timezone: IMeetingTypeLearnedField<string>;
    meetingCalendarDisplay: IMeetingTypeField<CalendarDisplay>;
    startOffsets?: IMeetingTypeLearnedField<IStartOffsetsSchema>; // 00, 15, 30, 45
}

export interface IClassType extends IMeetingTypeBase {
    style: MeetingTypeStyle.Class;
    // Booking Details
    maxAttendees?: IMeetingTypeField<number>;
}

/**
 * `all-internal-users-collective` - overlap availability of all
 * internal users invited to the meeting, **including** meeting organizer
 *
 * `all-internal-users-collective-no-organizer` - overlap availability of all
 * internal users invited to the meeting, **excluding** meeting organizer
 *
 * `some-internal-users-collective` - overlap availability of the users specified
 * in `availabilityUserIds`, may **include** or **exclude** meeting organizer
 *
 * `all-internal-users-round-robin` - flatten availability of all
 * internal users invited to the meeting, **including** meeting organizer.
 * Note: the ownership of the meeting will be **transfered** to one of the user's whose
 * availability was chosen.
 *
 * `all-internal-users-round-robin-no-organizer` - flatten availability of all
 * internal users invited to the meeting, **excluding** meeting organizer
 * Note: the ownership of the meeting will be **transfered** to one of the user's whose
 * availability was chosen.
 *
 * `some-internal-users-round-robin` - flatten availability of the users specified
 * in `availabilityUserIds`, may **include** or **exclude** meeting organizer.
 * Note: the ownership of the meeting will be **transfered** to one of the user's whose
 * availability was chosen.
 * if organizer is excluded this acts as ***On-behalf of***.
 */
export type AvailabilityType =
    | 'all-internal-users-collective'
    | 'all-internal-users-collective-no-organizer'
    | 'some-internal-users-collective'

    // round-robin
    | 'all-internal-users-round-robin' // future
    | 'all-internal-users-round-robin-no-organizer' // future
    | 'some-internal-users-round-robin'

    // on-behalf
    | 'on-behalf'
    | 'external-on-behalf';

// 'collective' represents all collective availability types, same with 'round-robin'
export type ExpandedAvailabilityType = AvailabilityType | 'collective' | 'round-robin';

// Availability types that rely on availabilityUserIds
export type AvailabilityTypesWithUserList = 'on-behalf' | 'collective' | 'round-robin';

export type AvailabilityPattern = 'book-over-internal-meetings';

export type CalendarDisplay = 'daily' | 'monthly';

export type MeetingTypeMode = {
    isExisting: boolean;
    isNew: boolean;
    isDuplicate: boolean;
    isShared?: boolean;
};

interface INamedType {
    name?: IMeetingTypeField<string>;
    icon?: IMeetingTypeField<string>;
}

export interface INamedMeetingType extends INamedType, IMeetingType {}
export interface INamedDefaultMeetingType extends INamedType, IDefaultMeetingType {}
export interface INamedClassType extends INamedType, IClassType {}

export type IAllNamedTypes =
    | INamedMeetingType
    | (INamedDefaultMeetingType & { style: MeetingTypeStyle.Meeting })
    | INamedClassType;

export function createEmptyNamedMeetingType() {
    const emptyNamedMeetingType: INamedMeetingType = {
        ...getEmptyBaseValues(),
        style: MeetingTypeStyle.Meeting,
        name: { value: '' },
        icon: { value: '' },
        startHour: { value: '', managed: false },
        endHour: { value: '', managed: false },
        availabilityWindows: {
            value: {
                monday: [],
                tuesday: [],
                wednesday: [],
                thursday: [],
                friday: [],
                saturday: [],
                sunday: []
            },
            managed: false
        },
        history: [],
        meetingLength: { value: 0, managed: false },
        disableTravelTime: { value: false },
        dateRange: { value: { type: 'text' as const, text: 'next 3 weeks' } },
        disableInvitingAttendees: { value: undefined },
        noticePeriodMinutes: { value: 0 },
        maxEventsPerPeriod: { value: undefined },
        bufferTimes: { value: { beforeMinutes: 0, afterMinutes: 0 } },
        enableHolidayBlocking: { value: false },
        availabilityType: { value: undefined },
        availabilityUserIds: { value: undefined },
        availabilityPattern: { value: undefined },
        disableTransferOwnership: { value: undefined },
        days: { value: undefined },
        allowEditingLocation: { value: false },
        enableRoundRobinSelection: { value: false }
    };

    return emptyNamedMeetingType;
}

export function createEmptyNamedClassType() {
    const emptyNamedClassType: IClassType = {
        ...getEmptyBaseValues(),
        style: MeetingTypeStyle.Class,

        // Booking Details
        meetingLength: { value: 60 },

        // Additional Customization
        maxAttendees: { value: 25 }
    };

    return emptyNamedClassType;
}

function getEmptyBaseValues() {
    const emptyCommonValues: IMeetingTypeBase = {
        locked: { value: false },

        // Name and Link
        defaultMeetingSubject: { value: '' },
        ondemand: { value: '' },

        // Booking Details
        bookingCalendarId: { value: '' },

        // Location & Conferencing
        location: { value: '' },
        startTime: { value: { kind: 'increment', increment: 15 } },
        videoProvider: {
            value: {
                id: '',
                staticUrl: ''
            }
        },

        // Additional Customization
        language: { value: undefined },
        description: { value: '' },
        confirmationUX: { value: { option: 'default' as ConfirmationOptionsType } },
        ask: { value: { questions: undefined } },
        trackingIds: { value: undefined }
    };

    return emptyCommonValues;
}

export function createEmptyNamedDefaultMeetingType() {
    const emptyNamedDefaultMeetingType = {
        emailHour: undefined,
        notifyForOverlap: undefined,
        cc_meeting: undefined,
        timezone: undefined,
        meetingCalendarDisplay: undefined,
        ...createEmptyNamedMeetingType()
    };

    return emptyNamedDefaultMeetingType;
}
